import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import EmptyState from '../components/ui/EmptyState';
import LoadingScreen from '../components/ui/LoadingScreen';
import { IconPlus } from '../components/ui/Icons';
import { useToast } from '../contexts/ToastContext';
import {
  getProductId,
  resolveTenantSelectValue,
  getTenantProductDbName,
  getTenantProductDbUri,
} from '../utils/ids';

function TenantProducts() {
  const toast = useToast();
  const [tenantProducts, setTenantProducts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenantProduct, setEditingTenantProduct] = useState(null);
  const [formData, setFormData] = useState({
    tenantId: '',
    productId: '',
    plan: '',
    status: 'active',
    db_name: '',
    db_uri: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tenantsRes, productsRes] = await Promise.all([
        api.post('/tenant/list', { page: 1, limit: 500 }),
        api.post('/product/list', {}),
      ]);
      setTenants(tenantsRes.data.data?.tenants || []);
      setProducts(productsRes.data.data?.products || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantProducts = async (tenantId) => {
    if (!tenantId) return;
    try {
      const response = await api.post('/tenant/product/list', { tenantId });
      setTenantProducts(response.data.data?.tenantProducts || []);
    } catch (error) {
      console.error('Error fetching tenant products:', error);
    }
  };

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    setFormData({ ...formData, tenantId });
    fetchTenantProducts(tenantId);
  };

  const handleCreate = () => {
    setEditingTenantProduct(null);
    setFormData({
      tenantId: formData.tenantId || '',
      productId: '',
      plan: '',
      status: 'active',
      db_name: '',
      db_uri: '',
    });
    setShowModal(true);
  };

  const handleEdit = (tp) => {
    setEditingTenantProduct(tp);
    setFormData({
      tenantId: resolveTenantSelectValue(tp.tenantId, tenants),
      productId: tp.productId,
      plan: tp.plan || '',
      status: tp.status || 'active',
      db_name: getTenantProductDbName(tp),
      db_uri: getTenantProductDbUri(tp),
    });
    setShowModal(true);
  };

  const buildTenantProductPayload = () => ({
    tenantId: formData.tenantId,
    productId: formData.productId,
    plan: formData.plan,
    status: formData.status,
    db_name: formData.db_name || null,
    db_uri: formData.db_uri || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenantProduct) {
        await api.post('/tenant/product/update', buildTenantProductPayload());
        toast.success('Tenant-Product updated successfully');
      } else {
        await api.post('/tenant/product/create', buildTenantProductPayload());
        toast.success('Tenant-Product created successfully');
      }
      setShowModal(false);
      fetchTenantProducts(formData.tenantId);
    } catch (error) {
      console.error('Error saving tenant-product:', error);
      toast.error(error.response?.data?.message || 'Failed to save tenant-product');
    }
  };

  const getProductName = (productId) => {
    const product = products.find((p) => getProductId(p) === productId);
    return product?.name || productId;
  };

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Tenant Products"
        description="Link products to tenants and configure database settings per relationship."
      />

      <div className="card filter-bar">
        <label className="filter-bar__label">
          Select tenant
          <select
            className="form-select"
            value={formData.tenantId}
            onChange={handleTenantChange}
          >
            <option value="">Select a tenant to view linked products</option>
            {tenants.map((tenant) => (
              <option key={tenant._id} value={tenant.subdomain}>
                {tenant.name} ({tenant.subdomain})
              </option>
            ))}
          </select>
        </label>
        {formData.tenantId && (
          <button type="button" onClick={handleCreate} className="btn btn--primary">
            <IconPlus width={18} height={18} />
            Link Product
          </button>
        )}
      </div>

      {formData.tenantId && (
        <div className="card">
          {tenantProducts.length === 0 ? (
            <EmptyState
              title="No linked products"
              description="Link a product to this tenant to get started."
              action={
                <button type="button" onClick={handleCreate} className="btn btn--primary">
                  <IconPlus width={18} height={18} />
                  Link Product
                </button>
              }
            />
          ) : (
            <div className="table-wrapper">
              <table className="data-table data-table--responsive">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>DB Name</th>
                    <th>DB URI</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantProducts.map((tp) => (
                    <tr key={`${tp.tenantId}-${tp.productId}`}>
                      <td data-label="Product">{getProductName(tp.productId)}</td>
                      <td data-label="Plan">{tp.plan || '—'}</td>
                      <td data-label="Status">
                        <StatusBadge status={tp.status} />
                      </td>
                      <td data-label="DB Name">{getTenantProductDbName(tp) || '—'}</td>
                      <td data-label="DB URI" className="cell-mono" title={getTenantProductDbUri(tp) || 'Using product default'}>
                        {getTenantProductDbUri(tp) || <span className="cell-muted">Product default</span>}
                      </td>
                      <td data-label="Actions" className="cell-actions">
                        <button type="button" onClick={() => handleEdit(tp)} className="btn btn--secondary btn--sm">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <Modal
          title={editingTenantProduct ? 'Edit Link' : 'Link Product'}
          subtitle="Configure the tenant-product relationship"
          onClose={() => setShowModal(false)}
          size="xl"
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Tenant <span className="required">*</span></label>
              <select
                className="form-select"
                value={formData.tenantId}
                onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                required
                disabled={!!editingTenantProduct}
              >
                <option value="">Select tenant</option>
                {tenants.map((tenant) => (
                  <option key={tenant._id} value={tenant.subdomain}>
                    {tenant.name} ({tenant.subdomain})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Product <span className="required">*</span></label>
              <select
                className="form-select"
                value={formData.productId}
                onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                required
                disabled={!!editingTenantProduct}
              >
                <option value="">Select product</option>
                {products.map((product) => {
                  const productId = getProductId(product);
                  return (
                    <option key={productId} value={productId}>
                      {product.name}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Plan</label>
              <input
                type="text"
                className="form-input"
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                  placeholder="Enter subscription plan"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Database Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.db_name}
                onChange={(e) => setFormData({ ...formData, db_name: e.target.value })}
                  placeholder="Enter database name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Database URI</label>
              <input
                type="text"
                className="form-input"
                value={formData.db_uri}
                onChange={(e) => setFormData({ ...formData, db_uri: e.target.value })}
                  placeholder="Enter database URI"
              />
              <span className="form-hint">Optional. Overrides the product default URI when set.</span>
            </div>
            <div className="modal__footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn--secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn--primary">
                {editingTenantProduct ? 'Save Changes' : 'Link Product'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default TenantProducts;
